<template>
  <page-list
    ref="EXTERNAL"
    v-if="settingLoaded"
    :list="list"
    :columns="filteredColumns"
    :export-data-options="exportDataOptions"
    :dataMapper="listDataMapper" />
</template>

<script>
import * as R from 'ramda'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { getTimeTableColumn, getCopyWithContentTableColumn } from '@/utils/common/tableColumn'
import { getNameFilter, getCreatedAtFilter } from '@/utils/common/tableFilter'
import SingleActionsMixin from '../mixins/singleActions'
import { FIELD_TYPE_PARSER } from '../constants'

export default {
  name: 'ReportCustomDataList',
  mixins: [WindowsMixin, ListMixin, SingleActionsMixin],
  props: {
    getParams: {
      type: Object,
      default: () => ({}),
    },
    listId: String,
    title: String,
    reportSettingId: String,
  },
  data () {
    return {
      columns: [],
      currentSetting: {},
      settingLoaded: true,
      list: this.$list.createList(this, {
        id: 'ext_resources_LIST',
        resource: 'ext_resources',
        apiVersion: 'v1',
        getParams: this.getParam,
        filterOptions: {
          name: getNameFilter(),
        },
        extraDataFecther: {
          noValue: async (data, params) => {
            return new Promise((resolve) => {
              resolve([{}])
            })
          },
        },
      }),
      exportDataItems: [],
    }
  },
  computed: {
    exportDataOptions () {
      return {
        downloadType: 'local',
        exportTitle: this.title,
        items: this.filteredColumns,
      }
    },
    filteredColumns () {
      const { attributes = [] } = this.currentSetting
      const columns = [
        getCopyWithContentTableColumn({ field: 'id', title: 'ID' }),
      ]
      attributes.map((item, index) => {
        const col = {
          field: item.name,
          title: `${item.name}${item.unit ? `(${item.unit})` : ''}`,
          formatter: ({ row }) => {
            return row[item.name] || '-'
          },
        }
        columns.push(col)
      })
      return [
        ...columns,
        {
          ...getTimeTableColumn(),
          formatter: ({ row }) => {
            if (row.created_at) {
              return this.$moment(row.created_at).format()
            }
            return ''
          },
        },
      ]
    },
  },
  watch: {
    reportSettingId: {
      handler: function (val) {
        this.fetchData()
      },
    },
    currentSetting: {
      handler: function (val) {
        if (val.id) {
          this.changeList()
        }
      },
      deep: true,
    },
  },
  created () {
    this.$sM = new this.$Manager('ext_resource_types', 'v1')
    this.fetchData()
  },
  methods: {
    changeList () {
      const filterOptions = {
        id: getNameFilter({ field: 'id', label: 'ID' }),
        created_at: getCreatedAtFilter(),
      }
      if (this.currentSetting) {
        const { attributes = [] } = this.currentSetting
        attributes.map(item => {
          if (item.value_type === 0 && item.choices && item.choices.length) {
            filterOptions[item.name.toLowerCase()] = {
              label: item.name,
              filter: true,
              dropdown: true,
              items: item.choices.map(txt => {
                return { label: txt, key: txt }
              }),
              formatter: (val) => {
                return `custom_filter|${item.name}|equals|${val}`
              },
            }
          } else if (item.value_type === 0) {
            filterOptions[item.name.toLowerCase()] = {
              label: item.name,
              filter: true,
              formatter: (val) => {
                return `custom_filter|${item.name}|contains|${val}`
              },
            }
          }
        })
      }
      this.list = this.$list.createList(this, {
        id: 'ext_associate_resources_LIST',
        resource: 'ext_resources',
        apiVersion: 'v1',
        getParams: this.getParam,
        filterOptions,
        genParamsCb: (params) => {
          const newParams = {}
          const { filter = [] } = params
          const newFilter = []
          let index = 0
          filter.flat(2).map(str => {
            if (str.startsWith('custom_filter')) {
              const list = str.split('|')
              newParams[`values.${index}.key`] = list[1]
              newParams[`values.${index}.condition`] = list[2]
              newParams[`values.${index}.value`] = list[3]
              index++
            } else {
              newFilter.push(str)
            }
          })
          return {
            ...params,
            filter: newFilter,
            ...newParams,
          }
        },
        extraDataFecther: {
          noValue: async (data, params) => {
            return new Promise((resolve) => {
              resolve([{}])
            })
          },
        },
        hiddenColumns: ['created_at'],
      })
      this.list.fetchData()
    },
    fetchData () {
      this.initSettingConfig()
    },
    async initSettingConfig () {
      try {
        const res = await this.$sM.get({
          id: this.reportSettingId,
        })
        this.currentSetting = res.data || {}
        this.settingLoaded = true
      } catch (err) { throw err }
    },
    refresh () {
      this.fetchData()
    },
    getParam () {
      const ret = {
        scope: this.$store.getters.scope,
        details: true,
        ext_resource_type_id: this.reportSettingId || 'default_report_setting_id',
        ...(R.is(Function, this.getParams) ? this.getParams() : this.getParams),
      }
      return ret
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'ReportCustomDataSidepage', {
        id: row.id,
        resource: 'ext_resources',
        getParams: this.getParam,
      }, {
        list: this.list,
      })
    },
    listDataMapper (data, extraData) {
      const { attributes = [] } = this.currentSetting
      for (const k in data) {
        if (data[k].data) {
          for (const j in data[k].data) {
            const target = attributes.filter(item => {
              return item.name === j && item.value_type + '' === '5'
            })
            if (target.length) {
              data[k].data[j] = data[k].data[j] ? FIELD_TYPE_PARSER.time.formatter(data[k].data[j]) : ''
            }
          }
        }
      }
      return data
    },
  },
}
</script>

<style lang="less" scoped>
.select-profix {
  .label {
    border-left: 1px solid #d9d9d9;
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
    line-height: 30px;
    padding: 0 10px;
    background: #f2f1f1;
    min-width: fit-content;
    height: 32px;
  }
}
</style>
